import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Input, Tag, Space, Typography, Divider, Alert } from "antd";
import threeCXWebRTC from "../services/3cxWebRTC";

const { Title, Text, Paragraph } = Typography;

const StatusTag = ({ status }) => {
  const colorMap = {
    idle: "default",
    connecting: "blue",
    connected: "cyan",
    registered: "green",
    disconnected: "orange",
    failed: "red",
    ringing: "blue",
    "in-call": "green",
    ended: "default",
  };
  return <Tag color={colorMap[status] || "default"}>{status.toUpperCase()}</Tag>;
};

export default function ThreeCXTest() {
  const [regStatus, setRegStatus] = useState("idle");
  const [callStatus, setCallStatus] = useState("idle");
  const [number, setNumber] = useState("");
  const [logs, setLogs] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const logEndRef = useRef(null);

  const config = {
    server: process.env.REACT_APP_3CX_SIP_SERVER || "(not set)",
    extension: process.env.REACT_APP_3CX_SIP_EXTENSION || "(not set)",
    user: process.env.REACT_APP_3CX_SIP_USER || "(not set)",
    passwordSet: !!process.env.REACT_APP_3CX_SIP_PASSWORD,
  };

  const log = (msg, level = "info") => {
    const time = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, { time, msg, level }]);
  };

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  useEffect(() => {
    threeCXWebRTC.on("connected", () => {
      setRegStatus("connected");
      log("Socket connected to SIP server");
    });
    threeCXWebRTC.on("disconnected", () => {
      setRegStatus("disconnected");
      log("Socket disconnected", "warn");
    });
    threeCXWebRTC.on("progress", () => {
      setCallStatus("ringing");
      log("Call ringing...");
    });
    threeCXWebRTC.on("confirmed", () => {
      setCallStatus("in-call");
      log("Call connected (audio flowing)", "success");
    });
    threeCXWebRTC.on("ended", () => {
      setCallStatus("ended");
      log("Call ended");
    });
    threeCXWebRTC.on("failed", (e) => {
      setCallStatus("failed");
      const cause = e?.cause || e?.message || "unknown";
      log(`Call failed: ${cause}`, "error");
      setErrorMsg(`Call failed: ${cause}`);
    });
    threeCXWebRTC.on("incomingCall", (session) => {
      log(`Incoming call from ${session?.remote_identity?.uri?.user || "unknown"}`);
    });
  }, []);

  const handleRegister = async () => {
    setErrorMsg("");
    setRegStatus("connecting");
    log(`Registering as ext ${config.extension} on ${config.server}...`);
    try {
      await threeCXWebRTC.initialize();
      setRegStatus("registered");
      log("SIP registration successful", "success");
    } catch (err) {
      setRegStatus("failed");
      const msg = err?.message || String(err);
      log(`Registration failed: ${msg}`, "error");
      setErrorMsg(msg);
    }
  };

  const handleCall = async () => {
    if (!number.trim()) {
      setErrorMsg("Enter a phone number first");
      return;
    }
    setErrorMsg("");
    setCallStatus("connecting");
    log(`Dialing ${number}...`);
    try {
      await threeCXWebRTC.makeCall(number.trim());
    } catch (err) {
      setCallStatus("failed");
      const msg = err?.message || String(err);
      log(`Call error: ${msg}`, "error");
      setErrorMsg(msg);
    }
  };

  const handleHangup = () => {
    threeCXWebRTC.endCall();
    log("Hang up requested");
    setCallStatus("ended");
  };

  const handleDisconnect = () => {
    threeCXWebRTC.disconnect();
    setRegStatus("idle");
    setCallStatus("idle");
    log("Disconnected from SIP server");
  };

  const inCall = callStatus === "ringing" || callStatus === "in-call" || callStatus === "connecting";

  return (
    <div style={{ maxWidth: 800, margin: "40px auto", padding: 20 }}>
      <Title level={2}>3CX WebRTC Test</Title>
      <Paragraph type="secondary">
        Debug page to verify that 3CX SIP registration and outbound calling work from the browser.
        Open the browser console for full JsSIP logs.
      </Paragraph>

      <Card title="Configuration (from .env)" size="small" style={{ marginBottom: 16 }}>
        <div><Text strong>SIP Server:</Text> <Text code>{config.server}</Text></div>
        <div><Text strong>Extension:</Text> <Text code>{config.extension}</Text></div>
        <div><Text strong>SIP User:</Text> <Text code>{config.user}</Text></div>
        <div><Text strong>Password:</Text> {config.passwordSet ? <Tag color="green">set</Tag> : <Tag color="red">missing</Tag>}</div>
      </Card>

      <Card title="Step 1 — Register" size="small" style={{ marginBottom: 16 }}>
        <Space>
          <Button type="primary" onClick={handleRegister} disabled={regStatus === "registered" || regStatus === "connecting"}>
            {regStatus === "connecting" ? "Connecting..." : "Register with 3CX"}
          </Button>
          <Button onClick={handleDisconnect} disabled={regStatus === "idle"}>Disconnect</Button>
          <StatusTag status={regStatus} />
        </Space>
      </Card>

      <Card title="Step 2 — Make a Call" size="small" style={{ marginBottom: 16 }}>
        <Space.Compact style={{ width: "100%" }}>
          <Input
            placeholder="e.g. 0977123456 or +260977123456"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            disabled={inCall}
          />
          <Button
            type="primary"
            onClick={handleCall}
            disabled={regStatus !== "registered" || inCall}
          >
            Call
          </Button>
          <Button danger onClick={handleHangup} disabled={!inCall}>Hang Up</Button>
        </Space.Compact>
        <div style={{ marginTop: 8 }}>
          <Text strong>Call status:</Text> <StatusTag status={callStatus} />
        </div>
      </Card>

      {errorMsg && (
        <Alert type="error" message={errorMsg} closable onClose={() => setErrorMsg("")} style={{ marginBottom: 16 }} />
      )}

      <Card title="Event Log" size="small">
        <div style={{ maxHeight: 300, overflowY: "auto", fontFamily: "monospace", fontSize: 12 }}>
          {logs.length === 0 && <Text type="secondary">No events yet.</Text>}
          {logs.map((l, i) => (
            <div
              key={i}
              style={{
                color:
                  l.level === "error" ? "#d4380d" :
                  l.level === "warn" ? "#d48806" :
                  l.level === "success" ? "#389e0d" : "#555",
              }}
            >
              [{l.time}] {l.msg}
            </div>
          ))}
          <div ref={logEndRef} />
        </div>
      </Card>

      <Divider />
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        Troubleshooting: if registration fails, common causes are (a) the extension is already registered on another device — log out of the 3CX web client first; (b) the SIP password in .env doesn't match the extension's WebRTC password (it's a separate field in 3CX admin); (c) the WSS port is blocked by your network firewall.
      </Paragraph>
    </div>
  );
}
